import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { Reward, SocialTask } from '../domain/types';
import { gamificationService } from '../domain/services/gamificationService';
import { socialService } from '../domain/services/socialService';
import { userRepository } from '../data/userRepository';
import { logger } from '../infrastructure/logger';
import { useAuth } from './auth';
import { useToast } from './ToastContext';

interface GamificationContextType {
  // Tasks
  completeTask: (task: SocialTask) => Promise<boolean>;

  // Rewards
  redeemReward: (reward: Reward) => Promise<boolean>;
  consumeReward: (rewardId: string) => void;

  // Raffles
  joinRaffle: (raffleId: string) => Promise<boolean>;

  // Levels (read-only helpers)
  getCurrentLevel: (points: number) => ReturnType<typeof gamificationService.getCurrentLevel>;
  getNextLevel: (currentLevelId: number) => ReturnType<typeof gamificationService.getNextLevel>;
  getPointsForNextLevel: (points: number) => ReturnType<typeof gamificationService.getPointsForNextLevel>;
  getProgressPercent: (points: number) => ReturnType<typeof gamificationService.getProgressPercent>;
  getAllLevels: () => ReturnType<typeof gamificationService.getAllLevels>;

}

const GamificationContext = createContext<GamificationContextType | null>(null);

export const GamificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, refreshUser } = useAuth();
  const { addToast } = useToast();

  const completeTask = useCallback(async (task: SocialTask): Promise<boolean> => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return false;
    }

    try {
      const result = await socialService.claimTaskReward(user.id, task);
      if (result.success) {
        await refreshUser();
        addToast(`¡Tarea completada! +${result.newPoints} puntos`, 'success');
        logger.info(`[GamificationContext] Task completed: ${task.id}, +${result.newPoints} points`);
        return true;
      }
      return false;
    } catch (error) {
      addToast('Error al completar tarea', 'error');
      logger.error('[GamificationContext] Error completing task', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user, refreshUser, addToast]);

  const redeemReward = useCallback(async (reward: Reward): Promise<boolean> => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return false;
    }

    if (user.points < reward.cost) {
      addToast('Puntos insuficientes', 'error');
      return false;
    }

    try {
      const result = await gamificationService.redeemReward(user.id, reward);
      if (result.success) {
        await refreshUser();
        addToast('¡Recompensa canjeada!', 'success');
        logger.info(`[GamificationContext] Reward redeemed: ${reward.id}, -${reward.cost} points`);
        return true;
      }
      addToast(result.message || 'Error al canjear', 'error');
      return false;
    } catch (error) {
      addToast('Error al canjear recompensa', 'error');
      logger.error('[GamificationContext] Error redeeming reward', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user, refreshUser, addToast]);

  const consumeReward = useCallback((rewardId: string) => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return;
    }

    try {
      const activeRewards = user.activeRewards || [];
      const filtered = activeRewards.filter((r: Reward) => r.id !== rewardId);
      userRepository.update(user.id, { activeRewards: filtered }).then(() => {
        refreshUser();
      });
      addToast('Recompensa utilizada', 'info');
      logger.info(`[GamificationContext] Reward consumed: ${rewardId}`);
    } catch (error) {
      addToast('Error al utilizar recompensa', 'error');
      logger.error('[GamificationContext] Error consuming reward', error instanceof Error ? error : new Error(String(error)));
    }
  }, [user, refreshUser, addToast]);

  const joinRaffle = useCallback(async (raffleId: string): Promise<boolean> => {
    if (!user) {
      addToast('Debes iniciar sesión', 'error');
      return false;
    }

    try {
      const result = await gamificationService.joinRaffle(user.id, raffleId);
      if (result.success) {
        await refreshUser();
        addToast('¡Te uniste al sorteo!', 'success');
        logger.info(`[GamificationContext] Joined raffle: ${raffleId}`);
        return true;
      }
      addToast(result.message || 'Error al unirte', 'error');
      return false;
    } catch (error) {
      addToast('Error al unirse al sorteo', 'error');
      logger.error('[GamificationContext] Error joining raffle', error instanceof Error ? error : new Error(String(error)));
      return false;
    }
  }, [user, refreshUser, addToast]);

  const getCurrentLevel = useCallback((points: number) => {
    return gamificationService.getCurrentLevel(points);
  }, []);

  const getNextLevel = useCallback((currentLevelId: number) => {
    return gamificationService.getNextLevel(currentLevelId);
  }, []);

  const getPointsForNextLevel = useCallback((points: number) => {
    return gamificationService.getPointsForNextLevel(points);
  }, []);

  const getProgressPercent = useCallback((points: number) => {
    return gamificationService.getProgressPercent(points);
  }, []);

  const getAllLevels = useCallback(() => {
    return gamificationService.getAllLevels();
  }, []);

  const value = useMemo(() => ({
    completeTask,
    redeemReward,
    consumeReward,
    joinRaffle,
    getCurrentLevel,
    getNextLevel,
    getPointsForNextLevel,
    getProgressPercent,
    getAllLevels
  }), [completeTask, redeemReward, consumeReward, joinRaffle, getCurrentLevel, getNextLevel, getPointsForNextLevel, getProgressPercent, getAllLevels]);

  return (
    <GamificationContext.Provider value={value}>
      {children}
    </GamificationContext.Provider>
  );
};

export const useGamification = () => {
  const context = useContext(GamificationContext);
  if (!context) {
    throw new Error('useGamification must be used within a GamificationProvider');
  }
  return context;
};

export default GamificationContext;
